// Ambiguous-looking characters (0/O, 1/l/I) are excluded so reception can
// read the password aloud/copy it without transcription mistakes.
const LOWER = 'abcdefghijkmnopqrstuvwxyz';
const UPPER = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
const DIGITS = '23456789';
const SYMBOLS = '!@#$%^&*()-_=+?';
const ALL = LOWER + UPPER + DIGITS + SYMBOLS;
const CATEGORIES = [LOWER, UPPER, DIGITS, SYMBOLS];
const LENGTH = 16;

function defaultRandomBytes(length) {
  // Fail closed: a temporary password is a security-sensitive credential.
  // If Web Crypto is unavailable we refuse to generate one with a weaker
  // source (`Math.random()` is not cryptographically secure) instead of
  // silently downgrading.
  if (typeof crypto === 'undefined' || typeof crypto.getRandomValues !== 'function') {
    throw new Error('No hay una fuente de aleatoriedad segura disponible (Web Crypto) para generar la contraseña temporal.');
  }
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return bytes;
}

export const GENERATION_FALLBACK_MESSAGE = 'No se pudo generar una contraseña temporal segura.';

/**
 * Turns a (possibly throwing) `generatePassword` call into UI-safe state:
 * `{ password, error }`. `generatePassword` (real or injected) is allowed to
 * throw — `generateTemporaryPassword` below fails closed when Web Crypto
 * isn't available (spec: never downgrade to a weaker source) — but the
 * message shown to the admin is always this same fixed fallback, never the
 * exception's own `.message`: exception text is not written for end users
 * (same rule `errorMessageFor` applies to API errors elsewhere) and could
 * vary by browser/environment or leak an internal detail. Shared by
 * `MemberForm` (Task 8) and `ResetPasswordForm` (Task 9) so both "generate a
 * temporary password up front" flows fail exactly the same safe way.
 */
export function safeGenerateTemporaryPassword(generatePassword) {
  try {
    return { password: generatePassword(), error: '' };
  } catch {
    return { password: '', error: GENERATION_FALLBACK_MESSAGE };
  }
}

/**
 * Generates a 16-character temporary password guaranteed to contain a
 * lowercase letter, an uppercase letter, a digit and a symbol. `randomBytes`
 * is injectable so tests (and any deterministic preview) can supply a fixed
 * byte source instead of depending on real entropy.
 */
export function generateTemporaryPassword({ randomBytes = defaultRandomBytes } = {}) {
  const bytes = randomBytes(LENGTH * 2);
  let cursor = 0;
  const nextByte = () => {
    const byte = bytes[cursor % bytes.length];
    cursor += 1;
    return byte;
  };

  // Guarantee one character from each required category by construction,
  // then fill the rest from the full alphabet and shuffle. The shuffle can
  // never remove the guaranteed characters, only relocate them.
  const chars = CATEGORIES.map(category => category[nextByte() % category.length]);
  while (chars.length < LENGTH) {
    chars.push(ALL[nextByte() % ALL.length]);
  }
  for (let i = chars.length - 1; i > 0; i -= 1) {
    const j = nextByte() % (i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join('');
}
